import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import { reportToSegment, types, eventNames } from '@smartcar/morse';
import { withRouter } from 'react-router-dom';

import { Banner, EmptyList, InlineLinks, InlineStyledText, LogTable, Modal, Spinner, UpgradeButton } from '../../../../../../components';

import { gatedFeatureData } from '../../../../../../services/featureGate';
import FEATURES from '../../../../../../services/featureGate/features';

import { getStatusText } from '../../../Webhooks/utils/webhookConfig';
import { Status } from '../../../Webhooks/components/WebhooksStatus/components/WebhooksStatusRow/components';
import staticText from '../../../../../../localization/Application/VehicleManagement/subscribeModal';
import { webhooksHeaders, PAGE_SIZE } from './utils';
import { useDelay } from '../../../../../../hooks';
import { handleUpgradeClick } from '../../../../../../components/UpsellModal/UpsellModal';

const SubscribeModal = ({
  closeModal,
  applicationId,
  featureSetId,
  rolePermissions,
  webhooks: rawWebhooks,
  actions: {
    fetchWebhooksRequest,
    subscribeVehiclesToWebhooks,
  },
  userVehicles,
  isFetchingWebhooks,
  isSubscribing,
  history,
}) => {
  const [paginationModel, setPaginationModel] = useState({ pageSize: PAGE_SIZE, page: 0 });
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const { afterDelay: showLoading } = useDelay();

  const isGated = gatedFeatureData(FEATURES.SCHEDULED_WEBHOOKS, featureSetId);
  const canWriteBilling = rolePermissions.includes('write_billing');
  const canManageVehicles = rolePermissions.includes('write_vehicle_management');

  useEffect(() => {
    reportToSegment(types.TRACK, eventNames.modalOpened, {
      label: 'subscribe vehicles to webhooks',
      title: staticText.title,
    });
    fetchWebhooksRequest();
  }, []);

  const handleRowClick = (row) => {
    const webhookId = row.id;
    setSelectedWebhook(webhookId === selectedWebhook ? null : webhookId);
  };

  const handleSubscribe = () => {
    const formattedUserVehicles = userVehicles.map(uv => ({
      userId: uv.userId,
      vehicleId: uv.vehicleId,
    }));
    reportToSegment(types.TRACK, eventNames.formSubmitted, {
      label: 'subscribe vehicles to webhooks',
      form_content: {
        userVehicles: formattedUserVehicles,
        webhookIds: [selectedWebhook],
      },
    });
    subscribeVehiclesToWebhooks({
      userVehicles: formattedUserVehicles,
      // Currently only one webhook ID is supported
      webhookIds: [selectedWebhook],
    });
  };

  const handleCreateWebhookClick = () => {
    reportToSegment(types.TRACK, eventNames.linkClicked, {
      section: 'article',
      style: 'cta primary',
      label: '[subscribe modal] create webhook',
      text: staticText.emptyState.addWebhook,
      path: `/apps/${applicationId}/webhooks?add-webhook=true`,
    });
  };

  const webhooks =
    rawWebhooks
      .filter(webhook => webhook.version === '4.0')
      .map((webhook) => {
        return {
          ...webhook,
          radioButton: {
            checked: selectedWebhook === webhook.id,
            handleClick: null, // let the event propagate to handleRowClick
          },
        };
      });

  const legacyWebhooks = rawWebhooks
    .filter(webhook => webhook.version !== '4.0');

  const renderCellMapping = {
    status: (params) => {
      const statusText = getStatusText(params.value);
      return (
        <Status
          statusText={statusText}
          hasError={params.row.disabledReason === 'error'}
          disabled={params.row.disabled}
        />
      );
    },
  };

  const upgradeCta = (() => {
    if (!isGated) return null;
    if (canWriteBilling) {
      return (
        <UpgradeButton
          text={staticText.featureGated.upgradeCta}
          feature={FEATURES.SCHEDULED_WEBHOOKS}
          handleClick={() => handleUpgradeClick({
            feature: FEATURES.SCHEDULED_WEBHOOKS,
            history,
          })}
        />
      );
    }
    return (
      <Banner severity="gated" styles={{ marginBottom: 1 }}>
        <Typography variant="body2">{staticText.featureGated.contactOwnersToUpgrade}</Typography>
      </Banner>
    );
  })();

  const content = (
    <React.Fragment>
      {webhooks.length > 0 &&
        <React.Fragment>
          <Typography mt={1} mb={3} variant="body2">
            {
              // eslint-disable-next-line no-nested-ternary
              !canManageVehicles
                ? staticText.cannotManageVehicles
                : userVehicles.length > 1
                  ? staticText.subscribeVehicles(userVehicles.length)
                  : <InlineStyledText
                      text={staticText.subscribeVehicleByName(userVehicles[0].displayName)}
                  />
            }
          </Typography>
          <LogTable
            pageInfo={{
              rows: webhooks,
              columns: webhooksHeaders,
              rowCount: webhooks.length,
            }}
            pageSizeOptions={[PAGE_SIZE]}
            handlePaginationModelChange={setPaginationModel}
            paginationModel={paginationModel}
            loading={showLoading && isFetchingWebhooks}
            paginationMode="client"
            hideFooter={webhooks.length <= PAGE_SIZE}
            handleRowClick={handleRowClick}
            renderCellMappingOverrides={renderCellMapping}
          />
          <Box display="flex" alignItems="center" mt={4.5} gap={1}>
            {legacyWebhooks.length > 0 && (
              <Typography variant="body2">
                <InlineLinks
                  text={staticText.legacyWebhooks}
                />
              </Typography>
            )}
            <Button sx={{ marginLeft: 'auto' }} disabled={isSubscribing || !canManageVehicles} onClick={closeModal} variant="outlined" color="secondary">
              {staticText.cancel}
            </Button>
            <Button disabled={isSubscribing || !canManageVehicles} onClick={handleSubscribe} variant="outlined" color="primary">
              {staticText.subscribe}
            </Button>
          </Box>
        </React.Fragment>
      }
      {webhooks.length === 0 && isFetchingWebhooks &&
        <Box display="flex" minHeight={300} alignItems="center" justifyContent="center">
          <Spinner size="small" />
        </Box>
      }
      {webhooks.length === 0 && !isFetchingWebhooks &&
        <EmptyList
          text={staticText.emptyState.noWebhooks}
          additionalTexts={legacyWebhooks.length > 0 && (
            <Typography variant="body2" mt={1}>
              <InlineLinks
                text={staticText.legacyWebhooks}
              />
            </Typography>
          )}
          cta={{
            text: staticText.emptyState.addWebhook,
            path: `/apps/${applicationId}/webhooks?add-webhook=true`,
            onClick: handleCreateWebhookClick,
          }}
          upgradeCta={upgradeCta}
          wrapperStyles={{
            border: 'none',
          }}
          dashboardPermission="write_vehicle_management"
        />
      }
    </React.Fragment>
  );

  return (
    <Modal
      title={staticText.title}
      toggleModal={closeModal}
      maxWidth="lg"
      content={content}
      divider={false}
      hideClose
    />
  );
};

export default withRouter(SubscribeModal);

SubscribeModal.propTypes = {
  applicationId: string.isRequired,
  featureSetId: string.isRequired,
  rolePermissions: arrayOf(string).isRequired,
  closeModal: func.isRequired,
  webhooks: arrayOf(shape({
    id: string.isRequired,
    name: string.isRequired,
    status: string.isRequired,
    updatedAt: string.isRequired,
    version: string.isRequired,
  })).isRequired,
  actions: shape({
    fetchWebhooksRequest: func.isRequired,
    subscribeVehiclesToWebhooks: func.isRequired,
  }).isRequired,
  userVehicles: arrayOf(shape({
    userId: string.isRequired,
    vehicleId: string.isRequired,
    displayName: string.isRequired,
  })).isRequired,
  isFetchingWebhooks: bool.isRequired,
  isSubscribing: bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  history: object.isRequired,
};
